<template>
  <div v-scroll="scrolling">
    <custom-text :msg="customText" :customValue="studentData" class="mt-3"></custom-text>
    <v-container grid-list-sm style="padding-bottom: 64px;">
      <v-layout row wrap align-end class="mt-3">
        <v-flex xs12 sm5 md4>
          <date-range-picker :dateRange="{startDate: fromDate, endDate: toDate}"
                             @updated="setDateRange"/>
          <span v-if="dateRangeInvalid" class="caption" style="color: red">{{$t('invalid_date_range')}}</span>
        </v-flex>
        <v-flex xs12 sm6 md7 offset-sm1 class="text-xs-right">
          <div v-if="attendanceAverage >= 0">{{$t('attendance.average')}} <span style="font-size: 20px">{{attendanceAverage}}%</span>
          </div>
        </v-flex>
      </v-layout>
      <v-layout row wrap pt-3>
        <v-flex xs12 mb-2>
          <h3>{{$t('attendance.summary.title')}}</h3>
        </v-flex>
        <v-flex xs6 md9 class="hidden-xs-only body-2">
          {{$t('attendance.summary.description')}}
        </v-flex>
        <v-flex xs2 md1 class="hidden-xs-only text-xs-right body-2">
          {{$t('attendance.summary.expected')}}
        </v-flex>
        <v-flex xs2 md1 class="hidden-xs-only text-xs-right body-2">
          {{$t('attendance.summary.attended')}}
        </v-flex>
        <v-flex xs2 md1 class="hidden-xs-only text-xs-right body-2">
          {{$t('attendance.summary.percentage')}}:
        </v-flex>
      </v-layout>
      <v-divider></v-divider>
      <div :style="{ backgroundColor: noInteractionsColor}" class="no-data"
           v-if="studentAttendanceSummary.length === 0 && allLoaded">
        <span :style="{color: getTextColor(noInteractionsColor)}">{{$t('attendance.no_data_found')}}</span>
      </div>
      <student-attendance-summary-row v-for="(summary, index) in studentAttendanceSummary" :key="'sas'+index"
                                      :attendanceSummary="summary">
      </student-attendance-summary-row>
      <v-flex xs12 mt-4>
        <h3>{{$t('attendance.details')}}</h3>
      </v-flex>
      <v-layout row wrap pt-2>
        <v-flex sm2 xs3>
          {{$t('attendance.date')}}
        </v-flex>
        <v-flex md6 sm5 xs6>
          {{$t('attendance.description')}}
        </v-flex>
        <v-flex sm3 hidden-xs-only>
          {{$t('attendance.status_description')}}
        </v-flex>
        <v-flex md1 sm2 xs3>
          {{$t('attendance.status')}}
          <v-menu attach left open-on-hover style="vertical-align: top; position: relative; top: -2px;">
            <v-btn id="attendance_legend_button" small icon slot="activator">
              <v-icon>info_outline</v-icon>
            </v-btn>
            <v-card>
              <v-container class="pt-2 pl-2 pr-2 pb-0">
                <v-list>
                  <v-list-tile-content>
                    <v-list-tile-title class="mb-2">
                      <h2>{{$t('attendance.status_legend')}}</h2>
                    </v-list-tile-title>
                    <v-divider></v-divider>
                    <v-list-tile-sub-title class="mt-1 mb-1" v-for="(item, key) in legend" :key="key">
                    <span class='text--primary'>
                      <v-icon>{{item.icon}}</v-icon> - {{$t(item.msg)}}
                    </span>
                    </v-list-tile-sub-title>
                  </v-list-tile-content>
                </v-list>
              </v-container>
            </v-card>
          </v-menu>
        </v-flex>
      </v-layout>
      <v-divider></v-divider>
      <div :style="{ backgroundColor: noInteractionsColor}" class="no-data"
           v-if="studentAttendance.length === 0 && allLoaded">
        <span :style="{color: getTextColor(noInteractionsColor)}">{{$t('attendance.no_data_found')}}</span>
      </div>
      <student-attendance-row v-for="(attendance, index) in studentAttendance" :attendance="attendance" :key="index">
      </student-attendance-row>
    </v-container>
  </div>
</template>

<script>
  import {
    GET_STUDENT_ATTENDANCE_AVERAGE,
    GET_STUDENT_ATTENDANCE_LIST,
    GET_STUDENT_ATTENDANCE_LIST_NEXT,
    GET_STUDENT_ATTENDANCE_SUMMARY_LIST
  } from '../../store/action-types'
  import { SET_STUDENT_ATTENDANCE_DATES, SET_STUDENT_ATTENDANCE_STUDENT } from '../../store/mutation-types'
  import Colors from '../../mixins/Colors'
  import DateRangePicker from '../date/DateRangePicker'
  import { mapState } from 'vuex'
  import StudentAttendanceRow from './StudentAttendanceRow'
  import StudentAttendanceSummaryRow from './StudentAttendanceSummaryRow'
  import CustomText from '../help/CustomText'
  import Roles from '../../mixins/Roles'
  import moment from 'moment-es6'

  const flagLegend = [
    {icon: 'mdi-check', msg: 'attendance.flag.attended'},
    {icon: 'mdi-window-close', msg: 'attendance.flag.not_attended'},
    {icon: 'mdi-calendar-check', msg: 'attendance.flag.mandatory'}
  ]

  export default {
    name: 'student-attendance',
    props: ['studentId', 'active'],
    mixins: [Colors, Roles],
    data () {
      return {
        initialLoaded: false,
        legend: flagLegend,
        noInteractionsColor: this.$vuetify.theme.info,
        dateRangeInvalid: false
      }
    },
    computed: {
      ...mapState({
        studentAttendance: state => state.attendance.studentAttendance,
        studentAttendanceSummary: state => state.attendance.studentAttendanceSummary,
        studentAttendanceAverage: state => state.attendance.studentAttendanceAverage,
        fromDate: state => state.attendance.fromDate,
        toDate: state => state.attendance.toDate,
        loadingList: state => state.attendance.loadingList,
        allLoaded: state => state.attendance.allLoaded,
        studentData: state => state.student
      }),
      attendanceAverage () {
        return this.studentAttendanceAverage.toFixed(1)
      },
      customText () {
        let role = this.isAdmin ? 'admin' : (this.isStaff ? 'staff' : 'student')
        return 'custom-text.' + role + '.attendance'
      }
    },
    watch: {
      active (nowActive) {
        if (nowActive && !this.initialLoaded) { // Load the data when tab first selected
          this.loadList()
        }
      }
    },
    methods: {
      setDateRange (range) {
        let diff = moment(range.endDate).diff(moment(range.startDate), 'days')
        if (diff >= 0) {
          this.dateRangeInvalid = false
          this.$store.commit(SET_STUDENT_ATTENDANCE_DATES, {
            fromDate: range.startDate, toDate: range.endDate
          })
          this.loadList()
        } else {
          this.dateRangeInvalid = true
        }
      },
      scrolling (event) {
        if (this.active && !this.loadingList && !this.allLoaded) {
          const scrollTop = window.scrollY
          const totalScroll = document.body.scrollHeight
          const clientHeight = window.innerHeight
          const leftToScroll = totalScroll - clientHeight - scrollTop
          if (leftToScroll < 300) {
            this.$store.dispatch(GET_STUDENT_ATTENDANCE_LIST_NEXT)
          }
        }
      },
      loadList () {
        this.$store.dispatch(GET_STUDENT_ATTENDANCE_LIST)
        this.$store.dispatch(GET_STUDENT_ATTENDANCE_AVERAGE)
        this.$store.dispatch(GET_STUDENT_ATTENDANCE_SUMMARY_LIST)
      }
    },
    mounted () {
      this.$store.commit(SET_STUDENT_ATTENDANCE_STUDENT, this.studentId)
      if (document.URL.endsWith('attendance')) {
        this.loadList()
      }
    },
    components: {
      StudentAttendanceSummaryRow,
      DateRangePicker,
      StudentAttendanceRow,
      CustomText
    }
  }
</script>

<style>
  .blank-icon {
    width: 24px;
    display: inline-block;
  }

  #attendance_legend_button {
    margin: 0px;
  }
</style>



// WEBPACK FOOTER //
// src/components/student/StudentAttendance.vue